import React, { useEffect, useMemo } from 'react';
import { AddressField } from '@flos/react-ui';
import useAppState from '../../state/appState';
import { ElementValue } from '../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { AddressField as AddressFieldType, FormValues, FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import { getIsValid, getValidationErrors } from '../../validators/validator-mapping';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValueHuman } from '../../types/element-values';
import { isRequired } from '../../helpers/isRequired';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getValue } from '../values/Get';
import { getMinMaxLength } from '../../helpers/getMinMaxLength';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';

const Addressfield = ({ elementId, caption, placeholder, helpText }: AddressFieldType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const value = Addressfield.getValueOrDefault(formValues, elementId);
    const [errorMessage, setErrorMessage] = React.useState('');

    const [fieldHasHadFocusAtLeastOnce, setFieldHasHadFocusAtLeastOnce] = React.useState(false);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (!fieldHasHadFocusAtLeastOnce) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    useEffect(() => {
        doValidation();
        // eslint-disable-next-line
    }, [value]);

    const isRequiredField = isRequired(formConfig, elementId);

    const isValid = Addressfield.isValidField(formConfig, formValues, elementId, fieldHasHadFocusAtLeastOnce);
    const { minLength, maxLength } = getMinMaxLength(formConfig, elementId);

    // The address component should be memorized to ensure render once
    // If render more than once the suggestion box will be displayed twice
    // The AddressField Flos component is using a thrid part component (dawa-autocomplete2)
    const memorizedAdressComponent = useMemo(
        () => (
            <AddressField
                defaultValue={value}
                fieldEvent={(data) => {
                    //get adrress from DAWA service
                    if (data.address) {
                        setFormValue(elementId, data.address);
                    }
                }}
                onBlur={() => {
                    doValidation();
                }}
                onFocus={() => {
                    setFieldHasHadFocusAtLeastOnce(true);
                }}
                isValid={isValid}
                errorText={errorMessage}
                id={elementId}
                key={elementId}
                label={placeholder}
                helpText={helpText}
                required={isRequiredField}
                aria-label={caption}
                data-testid="addressfield"
                minLength={minLength}
                maxLength={maxLength}
                onChange={(event) => {
                    //FieldEvent method will not be triggered if user enters empty string
                    //Thus onChange method is used to specifically handle empty string scenario
                    //Non-empty string input will be handle in fieldEvent
                    if (!event.target.value) {
                        setFormValue(elementId, '');
                    }
                }}
            />
        ),
        /* eslint-disable react-hooks/exhaustive-deps */
        [errorMessage, isValid]
    );

    return (
        <>
            {caption && <h6>{caption}</h6>}

            {/* Call the memoized address box */}
            {memorizedAdressComponent}
        </>
    );
};

Addressfield.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as AddressFieldType;
    const { summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);

    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined) {
        return <></>;
    }

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={value} />;
};

Addressfield.getValueSubmit = (formValues: FormValues, elementId: string, label: string): ClaimFormSubmitFieldValue => {
    const valueObj = Addressfield.getValue(formValues, elementId);
    const valueStr = Addressfield.getValueHuman(formValues, elementId);

    const valueId = { string: valueObj };

    return getClaimFormSubmitValue('textfield', elementId, label, valueStr, valueId);
};

Addressfield.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

Addressfield.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = Addressfield.getValue(formValues, elementId);
    if (value === undefined) {
        return '';
    }
    return value;
};

Addressfield.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const valueObj = Addressfield.getValue(formValues, elementId);

    const valueStr = valueObj;

    return valueStr;
};

Addressfield.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    return getIsValid(formConfig, formValues, elementId);
};

Addressfield.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHasHadFocusAtLeastOnce) {
        return undefined;
    }

    const isValid = Addressfield.isValid(formConfig, formValues, elementId);

    return isValid;
};

export default Addressfield;
